// ── Device relay browser client (TPT495) ──
//
// The page's one socket to /api/device-relay/web: lifecycle events for the projects a task view
// watches, start/stop commands, and the terminal attachments. The contract is
// ai/architecture/tt-device-tracking.md § Device relay. Everything the browser would otherwise
// inject (socket class, token, clock, timers) is a parameter, so this module imports nothing and
// runs under node:test.
//
// Default traffic stays minimal: no socket exists until a caller watches a project or attaches,
// an idle connection answers the relay's protocol pings and sends nothing else, and app-level
// `ping` frames (half-open detection) run only while a terminal is attached. The socket is
// dropped IDLE_CLOSE_MS after the last consumer leaves, so moving between tasks keeps it.

const SUBPROTOCOL = 'tipatask-relay.v1';
// A browser cannot set Authorization on a WebSocket: the JWT is offered as a second subprotocol,
// which keeps it out of the URL. Cookie sessions have no JS-readable token and offer none.
const BEARER_SUBPROTOCOL_PREFIX = 'bearer.';
const RELAY_PATH = '/api/device-relay/web';
const WS_OPEN = 1;

export const RECONNECT_BASE_MS = 1000;
export const RECONNECT_MAX_MS = 30000;
export const RATE_LIMITED_RETRY_MS = 60000;
export const IDLE_CLOSE_MS = 30000;
// Above the relay's own COMMAND_TIMEOUT_MS (30000), so its device_timeout answer arrives first.
export const COMMAND_TIMEOUT_MS = 45000;
export const PING_INTERVAL_MS = 30000;
export const PONG_TIMEOUT_MS = 10000;
export const REPLAY_BYTES = 131072;
// A paste larger than this is refused rather than queued: at the relay's sustained input rate it
// would still be typing into the agent after the user moved on.
export const MAX_INPUT_QUEUE_BYTES = 1048576;

// JSON writes a control character as six bytes, and a frame over the relay's limit closes the socket.
const WORST_CASE_JSON_BYTES = 6;
const FRAME_ENVELOPE_BYTES = 256;

// Used until `ready` announces the real values.
const DEFAULT_LIMITS = Object.freeze({
  max_web_frame_bytes: 32768,
  max_input_data_bytes: 8192,
  max_replay_bytes: 262144,
  input_bytes_per_second: 65536,
});

export function relayUrl(location) {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${RELAY_PATH}`;
}

export function relayProtocols(token) {
  return token ? [SUBPROTOCOL, `${BEARER_SUBPROTOCOL_PREFIX}${token}`] : [SUBPROTOCOL];
}

// crypto.randomUUID() exists only in secure contexts; a plain-HTTP dev host has getRandomValues().
export function newCommandId(crypto = globalThis.crypto) {
  const random = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint32Array(4)), word => word.toString(16)).join('-');
  return `web.${random}`;
}

// Exponential with jitter, so a relay restart does not bring every browser back at once.
export function reconnectDelay(attempt, random = Math.random) {
  const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.min(attempt, 10));
  return Math.round(backoff * (0.75 + random() * 0.5));
}

// What a close code asks of the client (tt-device-tracking.md § Close codes).
export function closeAction(code) {
  if (code === 4401) return 'renew';
  if (code === 4429) return 'backoff';
  if (code === 1009 || code === 4400 || code === 4403 || code === 4409) return 'stop';
  return 'retry';
}

function codePointBytes(codePoint) {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  return codePoint < 0x10000 ? 3 : 4;
}

export function utf8Length(text) {
  let bytes = 0;
  for (const char of text) bytes += codePointBytes(char.codePointAt(0));
  return bytes;
}

// Pieces of at most `maxBytes` UTF-8 bytes, cut between code points: the relay measures `input`
// in bytes, and a cut through a surrogate pair would corrupt the character.
export function splitUtf8(text, maxBytes) {
  const pieces = [];
  let piece = '';
  let size = 0;
  for (const char of text) {
    const bytes = codePointBytes(char.codePointAt(0));
    if (size + bytes > maxBytes) {
      pieces.push(piece);
      piece = '';
      size = 0;
    }
    piece += char;
    size += bytes;
  }
  if (piece) pieces.push(piece);
  return pieces;
}

export function createDeviceRelayClient(deps = {}) {
  const getToken = deps.getToken ?? (() => null);
  const getUserId = deps.getUserId ?? (() => null);
  const renewAuth = deps.renewAuth ?? (async () => {});
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? (id => clearTimeout(id));
  const now = deps.now ?? Date.now;
  const random = deps.random ?? Math.random;

  let socket = null;
  let socketUserId = null;
  // idle | connecting | open | reconnecting | auth | stopped
  let connection = 'idle';
  let everReady = false;
  let attempts = 0;
  let retryTimer = null;
  let idleTimer = null;
  let pingTimer = null;
  let pongTimer = null;
  let wakeTarget = null;
  let limits = { ...DEFAULT_LIMITS };
  let inputTokens = DEFAULT_LIMITS.input_bytes_per_second;
  let inputRefilledAt = 0;

  const projects = new Map();    // project id -> Set(listener)
  const attachments = new Map(); // session id -> attachment
  const commands = new Map();    // command id -> start/stop waiting for its command_result
  const attachCommands = new Map(); // command id -> attachment

  const hasDemand = () => projects.size > 0 || attachments.size > 0 || commands.size > 0;
  const isOpen = () => connection === 'open' && socket?.readyState === WS_OPEN;

  function send(frame) {
    if (!isOpen()) return false;
    socket.send(JSON.stringify(frame));
    return true;
  }

  function emitConnection() {
    const event = { type: 'connection', connection };
    for (const listeners of projects.values()) for (const listener of [...listeners]) listener(event);
    for (const attachment of [...attachments.values()]) attachment.listener(event);
  }

  function setConnection(next) {
    if (next === 'idle') everReady = false; // The next dial is a first one, not a recovery.
    if (connection === next) return;
    connection = next;
    emitConnection();
  }

  // ── Wake-ups ── The network coming back, or a hidden tab returning, retries at once instead
  // of waiting out the backoff. Bound only while something wants the socket.
  function onWake() {
    if (retryTimer === null) return;
    clearTimer(retryTimer);
    retryTimer = null;
    connect();
  }

  function onAuthRefreshed() {
    if (connection === 'auth') connect();
    else onWake();
  }

  function bindWake() {
    const target = deps.wakeTarget ?? globalThis.window;
    if (wakeTarget || typeof target?.addEventListener !== 'function') return;
    wakeTarget = target;
    target.addEventListener('online', onWake);
    target.addEventListener('focus', onWake);
    target.addEventListener('auth:refreshed', onAuthRefreshed);
  }

  function unbindWake() {
    if (!wakeTarget) return;
    wakeTarget.removeEventListener('online', onWake);
    wakeTarget.removeEventListener('focus', onWake);
    wakeTarget.removeEventListener('auth:refreshed', onAuthRefreshed);
    wakeTarget = null;
  }

  // ── Socket lifecycle ──
  function connect() {
    if (socket) return;
    const WebSocketImpl = deps.WebSocketImpl ?? globalThis.WebSocket;
    const location = deps.location ?? globalThis.location;
    setConnection(everReady ? 'reconnecting' : 'connecting');
    let ws;
    try {
      ws = new WebSocketImpl(relayUrl(location), relayProtocols(getToken()));
    } catch {
      scheduleRetry(reconnectDelay(attempts++, random));
      return;
    }
    socket = ws;
    socketUserId = getUserId();
    ws.onmessage = (event) => {
      if (socket !== ws || typeof event.data !== 'string') return;
      let frame;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }
      if (frame && typeof frame === 'object') onFrame(frame);
    };
    ws.onclose = (event) => {
      if (socket === ws) onSocketGone(event.code);
    };
    ws.onerror = () => {}; // `close` follows and owns the retry.
  }

  function scheduleRetry(delay) {
    if (retryTimer !== null) return;
    setConnection('reconnecting');
    retryTimer = setTimer(() => {
      retryTimer = null;
      connect();
    }, delay);
  }

  // Forgets the socket without waiting for its close handshake, which a half-open link never
  // finishes.
  function discardSocket() {
    const ws = socket;
    socket = null;
    socketUserId = null;
    if (!ws) return;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close(1000);
    } catch { /* Already closing. */ }
  }

  function clearHeartbeat() {
    if (pingTimer !== null) clearTimer(pingTimer);
    if (pongTimer !== null) clearTimer(pongTimer);
    pingTimer = null;
    pongTimer = null;
  }

  function onSocketGone(code) {
    discardSocket();
    clearHeartbeat();
    attachCommands.clear();
    for (const attachment of attachments.values()) resetAttachment(attachment);
    if (!hasDemand()) {
      setConnection('idle');
      unbindWake();
      return;
    }
    switch (closeAction(code)) {
      case 'renew':
        setConnection('auth');
        // A failed renewal leaves `auth` in place: api.js has already sent the user to sign in.
        Promise.resolve().then(renewAuth).then(() => {
          if (connection === 'auth' && hasDemand()) connect();
        }, () => {});
        break;
      case 'stop':
        setConnection('stopped');
        break;
      case 'backoff':
        scheduleRetry(RATE_LIMITED_RETRY_MS);
        break;
      default:
        scheduleRetry(reconnectDelay(attempts++, random));
    }
  }

  // Terminal input never crosses a reconnect: keys typed into a dead link would reach the agent
  // later, against a screen the user no longer sees.
  function resetAttachment(attachment) {
    attachment.live = false;
    attachment.replaying = false;
    attachment.resyncing = false;
    attachment.commandId = null;
    attachment.queue = [];
    attachment.queuedBytes = 0;
    if (attachment.pumpTimer !== null) clearTimer(attachment.pumpTimer);
    attachment.pumpTimer = null;
  }

  function ensureConnected() {
    if (idleTimer !== null) {
      clearTimer(idleTimer);
      idleTimer = null;
    }
    bindWake();
    // A lingering socket belongs to whoever opened it; another sign-in must not inherit it.
    if (socket && socketUserId !== null && getUserId() !== socketUserId) {
      discardSocket();
      clearHeartbeat();
      connection = 'idle';
    }
    if (connection === 'idle' || connection === 'stopped') {
      attempts = 0;
      connect();
    }
  }

  function afterRelease() {
    syncHeartbeat();
    if (hasDemand()) return;
    unbindWake();
    if (retryTimer !== null) {
      clearTimer(retryTimer);
      retryTimer = null;
    }
    if (!socket) {
      setConnection('idle');
    } else if (idleTimer === null) {
      idleTimer = setTimer(() => {
        idleTimer = null;
        if (hasDemand()) return;
        discardSocket();
        clearHeartbeat();
        setConnection('idle');
      }, IDLE_CLOSE_MS);
    }
  }

  // ── Heartbeat ── A browser cannot see protocol pings, so an attached terminal asks.
  function syncHeartbeat() {
    const wanted = connection === 'open' && attachments.size > 0;
    if (!wanted) {
      clearHeartbeat();
    } else if (pingTimer === null) {
      pingTimer = setTimer(() => {
        pingTimer = null;
        if (send({ type: 'ping' }) && pongTimer === null) {
          pongTimer = setTimer(() => {
            pongTimer = null;
            onSocketGone(1006);
          }, PONG_TIMEOUT_MS);
        }
        syncHeartbeat();
      }, PING_INTERVAL_MS);
    }
  }

  // ── Frames ──
  function onReady(frame) {
    if (frame.protocol !== 1) {
      discardSocket();
      setConnection('stopped');
      return;
    }
    const expected = getUserId();
    if (expected !== null && frame.user_id !== expected) {
      discardSocket();
      setConnection('stopped');
      return;
    }
    limits = { ...DEFAULT_LIMITS, ...frame.limits };
    inputTokens = limits.input_bytes_per_second;
    inputRefilledAt = now();
    attempts = 0;
    everReady = true;
    connection = 'open';
    for (const projectId of projects.keys()) send({ type: 'subscribe', project_id: projectId });
    // Same command ids: the relay and the device both deduplicate them, so a start that may have
    // landed before the link dropped cannot run twice.
    for (const { frame: pending } of commands.values()) send(pending);
    for (const attachment of attachments.values()) sendAttach(attachment);
    emitConnection();
    syncHeartbeat();
  }

  function sendAttach(attachment) {
    const commandId = newCommandId(deps.crypto ?? globalThis.crypto);
    attachment.commandId = commandId;
    attachment.live = false;
    attachment.replaying = false;
    attachCommands.set(commandId, attachment);
    send({
      type: 'attach',
      command_id: commandId,
      session_id: attachment.sessionId,
      replay_bytes: Math.min(REPLAY_BYTES, limits.max_replay_bytes),
    });
  }

  function requestResync(attachment) {
    if (attachment.resyncing || !isOpen()) return;
    attachment.resyncing = true;
    attachment.live = false;
    send({ type: 'detach', session_id: attachment.sessionId });
  }

  function removeAttachment(attachment) {
    resetAttachment(attachment);
    attachments.delete(attachment.sessionId);
  }

  function onAttachResult(commandId, frame) {
    const attachment = attachCommands.get(commandId);
    attachCommands.delete(commandId);
    if (attachment.commandId !== commandId || attachments.get(attachment.sessionId) !== attachment) return;
    if (frame.ok) {
      attachment.live = true;
      attachment.listener({ type: 'attach-ok' });
      pumpInput(attachment);
    } else if (frame.code === 'already_attached') {
      requestResync(attachment);
    } else if (frame.code === 'device_disconnected') {
      // The relay keeps this attachment and replays through it when the device returns.
      attachment.live = true;
    } else {
      removeAttachment(attachment);
      attachment.listener({ type: 'refused', code: frame.code ?? 'relay_unavailable' });
      afterRelease();
    }
  }

  function finishCommand(commandId, result) {
    const command = commands.get(commandId);
    if (!command) return;
    commands.delete(commandId);
    clearTimer(command.timer);
    command.resolve(result);
    afterRelease();
  }

  function onOutput(frame) {
    const attachment = attachments.get(frame.session_id);
    if (!attachment || attachment.resyncing || typeof frame.data !== 'string') return;
    if (frame.replay) {
      // Also how a relay-issued attach after a device reconnect shows itself: no command_result
      // reaches the browser, only a fresh replay that replaces what is on screen.
      if (!attachment.replaying) {
        attachment.replaying = true;
        attachment.listener({ type: 'reset' });
      }
      attachment.listener({ type: 'output', data: frame.data, replay: true });
      return;
    }
    // Live output the device sent before it handled this attach reaches the new attachment too;
    // the replay that follows the command_result already holds it.
    if (!attachment.live) return;
    attachment.replaying = false;
    attachment.listener({ type: 'output', data: frame.data, replay: false });
  }

  function onDetached(frame) {
    const attachment = attachments.get(frame.session_id);
    if (!attachment) return;
    if (frame.reason === 'client_detached' || frame.reason === 'not_attached') {
      if (attachment.resyncing) {
        attachment.resyncing = false;
        sendAttach(attachment);
      }
      return;
    }
    if (frame.reason === 'attach_refused') return; // The command_result carries the reason.
    removeAttachment(attachment);
    attachment.listener({ type: 'detached', reason: frame.reason });
    afterRelease();
  }

  function onError(frame) {
    if (frame.project_id !== undefined && frame.code === 'project_not_found') {
      emitProject(frame.project_id, { type: 'revoked', reason: frame.code });
      return;
    }
    const attachment = attachments.get(frame.session_id);
    if (attachment) attachment.listener({ type: 'input-refused', code: frame.code });
  }

  function emitProject(projectId, event) {
    for (const listener of [...(projects.get(projectId) ?? [])]) listener(event);
  }

  function onFrame(frame) {
    if (pongTimer !== null) {
      clearTimer(pongTimer);
      pongTimer = null;
    }
    switch (frame.type) {
      case 'ready':
        onReady(frame);
        break;
      case 'subscribed':
        emitProject(frame.project_id, { type: 'subscribed', sessions: frame.sessions ?? [], canWrite: frame.can_write === true });
        break;
      case 'session':
        emitProject(frame.project_id, { type: 'session', session: frame });
        attachments.get(frame.session_id)?.listener({ type: 'session', session: frame });
        break;
      case 'device':
        for (const projectId of projects.keys()) emitProject(projectId, { type: 'device', deviceId: frame.device_id, online: frame.online });
        break;
      case 'revoked':
        emitProject(frame.project_id, { type: 'revoked', reason: frame.reason });
        break;
      case 'command_result':
        if (attachCommands.has(frame.command_id)) onAttachResult(frame.command_id, frame);
        else finishCommand(frame.command_id, frame);
        break;
      case 'attached':
        attachments.get(frame.session_id)?.listener({ type: 'attached', readOnly: frame.read_only === true, holder: frame.holder });
        break;
      case 'controller':
        attachments.get(frame.session_id)?.listener({ type: 'controller', holder: frame.holder });
        break;
      case 'detached':
        onDetached(frame);
        break;
      case 'output':
        onOutput(frame);
        break;
      case 'output_gap':
        attachments.get(frame.session_id)?.listener({ type: 'gap' });
        break;
      case 'error':
        onError(frame);
        break;
      default: // ack, pong, unsubscribed: nothing to do.
    }
  }

  // ── Input ── Paced under the relay's sustained rate, one bucket per socket like the relay's.
  function refillInput() {
    const at = now();
    const rate = limits.input_bytes_per_second;
    inputTokens = Math.min(rate, inputTokens + ((at - inputRefilledAt) / 1000) * rate);
    inputRefilledAt = at;
  }

  function pumpInput(attachment) {
    if (attachment.pumpTimer !== null) return;
    while (attachment.queue.length && attachment.live && isOpen()) {
      refillInput();
      const { data, bytes } = attachment.queue[0];
      if (bytes > inputTokens) {
        const wait = Math.ceil(((bytes - inputTokens) / limits.input_bytes_per_second) * 1000);
        attachment.pumpTimer = setTimer(() => {
          attachment.pumpTimer = null;
          pumpInput(attachment);
        }, wait);
        return;
      }
      inputTokens -= bytes;
      attachment.queue.shift();
      attachment.queuedBytes -= bytes;
      attachment.seq += 1;
      send({ type: 'input', session_id: attachment.sessionId, seq: attachment.seq, data });
    }
  }

  function queueInput(attachment, data) {
    if (attachments.get(attachment.sessionId) !== attachment || !attachment.live || !isOpen()) return false;
    const total = utf8Length(data);
    if (attachment.queuedBytes + total > MAX_INPUT_QUEUE_BYTES) {
      attachment.listener({ type: 'input-refused', code: 'input_too_large' });
      return false;
    }
    const maxPiece = Math.min(
      limits.max_input_data_bytes,
      Math.floor((limits.max_web_frame_bytes - FRAME_ENVELOPE_BYTES) / WORST_CASE_JSON_BYTES),
    );
    for (const piece of splitUtf8(data, maxPiece)) {
      const bytes = utf8Length(piece);
      attachment.queue.push({ data: piece, bytes });
      attachment.queuedBytes += bytes;
    }
    pumpInput(attachment);
    return true;
  }

  // ── Public surface ──
  function watchProject(projectId, listener) {
    let listeners = projects.get(projectId);
    if (!listeners) {
      listeners = new Set();
      projects.set(projectId, listeners);
    }
    listeners.add(listener);
    ensureConnected();
    send({ type: 'subscribe', project_id: projectId }); // On a live socket: a fresh snapshot.
    let watching = true;
    return () => {
      if (!watching) return;
      watching = false;
      listeners.delete(listener);
      if (listeners.size === 0 && projects.get(projectId) === listeners) {
        projects.delete(projectId);
        send({ type: 'unsubscribe', project_id: projectId });
      }
      afterRelease();
    };
  }

  // Resolves with the command_result (`ok`, `code?`, `session_id?`), or device_timeout. Never
  // rejects. A command whose socket dropped goes out again, same id, once the socket is back.
  function runCommand(fields) {
    const commandId = newCommandId(deps.crypto ?? globalThis.crypto);
    const frame = { ...fields, command_id: commandId };
    return new Promise((resolve) => {
      const timer = setTimer(() => finishCommand(commandId, { ok: false, code: 'device_timeout' }), COMMAND_TIMEOUT_MS);
      commands.set(commandId, { frame, resolve, timer });
      ensureConnected();
      send(frame);
    });
  }

  function start({ deviceId, projectId, taskKey, agent = null }) {
    return runCommand({ type: 'start', device_id: deviceId, project_id: projectId, task_key: taskKey, agent });
  }

  function stop(sessionId) {
    return runCommand({ type: 'stop', session_id: sessionId });
  }

  // `listener` receives attach-ok, attached, controller, reset, output, gap, session, detached,
  // refused, input-refused and connection events until the handle's detach(), which stops the
  // stream and leaves the session running.
  function attach(sessionId, listener) {
    attachments.get(sessionId)?.handle.detach();
    const attachment = {
      sessionId,
      listener,
      live: false,
      replaying: false,
      resyncing: false,
      commandId: null,
      seq: 0,
      queue: [],
      queuedBytes: 0,
      pumpTimer: null,
      handle: null,
    };
    attachment.handle = {
      input: data => queueInput(attachment, data),
      resize(cols, rows) {
        if (attachments.get(sessionId) === attachment && attachment.live) send({ type: 'resize', session_id: sessionId, cols, rows });
      },
      resync() {
        if (attachments.get(sessionId) === attachment) requestResync(attachment);
      },
      detach() {
        if (attachments.get(sessionId) !== attachment) return;
        removeAttachment(attachment);
        send({ type: 'detach', session_id: sessionId });
        afterRelease();
      },
    };
    attachments.set(sessionId, attachment);
    ensureConnected();
    if (isOpen()) sendAttach(attachment);
    syncHeartbeat();
    return attachment.handle;
  }

  // A user asked to try again after the client stopped on its own.
  function retry() {
    if (retryTimer !== null) {
      clearTimer(retryTimer);
      retryTimer = null;
    }
    if (hasDemand() && !socket) {
      attempts = 0;
      connect();
    }
  }

  return {
    watchProject,
    start,
    stop,
    attach,
    retry,
    get connection() { return connection; },
    // What is held open, for leak checks.
    stats() {
      return {
        socket: socket !== null,
        projects: projects.size,
        attachments: attachments.size,
        commands: commands.size,
        wakeBound: wakeTarget !== null,
        heartbeat: pingTimer !== null || pongTimer !== null,
      };
    },
  };
}
